import type { AgentTemplate, ProjectAgent } from "@/api/agents";
import type {
  InputBarFocusedAgent,
  InputBarProvider,
} from "@/components/panels/input-bar-model-pill";
import type {
  AgentSession,
  SessionTokens,
  AvailableModelsMap,
} from "@/stores/companion-store";
import { contextUsagePercent } from "@/lib/context-usage";
import { inputBarConversationIdentity } from "@/lib/input-bar-conversation-identity";
import { followsStandardModel } from "@/lib/standard-model-policy";
import {
  getModelEffortConfig,
  resolveTemplateModel,
  type EffortValue,
  type RuntimeKey,
} from "@/lib/model-registry";

/** Picker tab key for an adapter and its source; managed OpenCode stays distinct. */
export function runtimeIdForSource(
  adapterType: RuntimeKey,
  runtimeSource: string | null | undefined,
): string {
  return adapterType === "opencode_cli" && runtimeSource
    ? `${adapterType}:${runtimeSource}`
    : adapterType;
}

export function providerRuntimeId(provider: InputBarProvider): string {
  return provider.runtimeId ?? provider.type;
}

/** User-facing chat always belongs to the built-in Denker agent. */
export function resolveDenkerProjectAgent(projectAgents: ProjectAgent[]) {
  return (
    projectAgents.find(
      (agent) =>
        agent.is_template_builtin &&
        agent.template_name.toLowerCase() === "denker",
    ) ?? null
  );
}

export function buildDenkerComposerAgentInfo({
  projectAgents,
  agentTemplates,
  sessionTokens,
  agentSessions,
  currentSpaceId,
  availableModels = {},
}: {
  projectAgents: ProjectAgent[];
  agentTemplates: AgentTemplate[];
  sessionTokens: Record<string, SessionTokens>;
  agentSessions: Record<string, AgentSession>;
  currentSpaceId: string | null | undefined;
  availableModels?: AvailableModelsMap;
}): InputBarFocusedAgent | null {
  const pa = resolveDenkerProjectAgent(projectAgents);
  if (!pa) return null;
  const template = agentTemplates.find((t) => t.id === pa.agent_template);

  // Template is authoritative: backend execution reads only from
  // AgentTemplate (see execution_dispatch.resolve_agent_for_execution),
  // and both the agent settings page and this pill's write path target
  // the template. ProjectAgent.project_specific_config is deliberately
  // ignored — see resolveTemplateModel.
  const { adapterType, model: resolvedModel } = resolveTemplateModel(
    template,
    availableModels,
  );
  const effectiveModel = resolvedModel ?? "";
  const runtimeSource =
    typeof template?.adapter_config?.runtime_source === "string"
      ? template.adapter_config.runtime_source
      : "";
  const runtimeId = runtimeIdForSource(adapterType, runtimeSource);

  // Effort is validated against the selected model's accepted values.
  // If the stored value is missing or doesn't match what the model
  // supports (e.g. migrating a Claude agent to a Codex model), use
  // "default" (meaning: don't override the model's own default).
  const rawEffort = template?.adapter_config?.thinking_effort ?? null;
  const modelEffortCfg = getModelEffortConfig(effectiveModel);
  const autoUpgraded =
    template &&
    followsStandardModel(template) &&
    template.adapter_config?.model !== effectiveModel;
  const supportedEfforts =
    availableModels[adapterType]?.find((entry) => entry.id === effectiveModel)
      ?.reasoning_efforts ?? modelEffortCfg.values;
  const effort: InputBarFocusedAgent["effort"] =
    rawEffort && supportedEfforts.includes(rawEffort)
      ? (rawEffort as EffortValue)
      : autoUpgraded && rawEffort
        ? supportedEfforts.includes("medium")
          ? "medium"
          : ((supportedEfforts[0] as EffortValue | undefined) ?? "default")
        : "default";

  // Context % from live session tokens streamed on execution.completed.
  // Keyed by `${agent_key}:${space_id}` — same shape as agentSessions.
  let contextPercent: number | null = null;
  const agentKey = pa.template_name?.toLowerCase() ?? "";
  const tokenSnapshot =
    agentKey && currentSpaceId
      ? sessionTokens[`${agentKey}:${currentSpaceId}`]
      : undefined;
  if (agentKey && currentSpaceId) {
    const tokens = tokenSnapshot;
    const contextMax = tokens?.max;
    contextPercent = contextUsagePercent({
      used: tokens?.contextUsed,
      input: tokens?.lastInput,
      output: tokens?.lastOutput,
      max: contextMax,
    });
  }

  const activeRuntime =
    agentKey && currentSpaceId
      ? agentSessions[`${agentKey}:${currentSpaceId}`]
      : undefined;
  const conversationIdentity = inputBarConversationIdentity(
    activeRuntime,
    tokenSnapshot,
  );
  return {
    id: pa.id,
    agentName: pa.template_name || "Agent",
    spaceId: currentSpaceId || undefined,
    ...conversationIdentity,
    adapterType,
    runtimeId,
    model: effectiveModel,
    effort,
    contextPercent,
  };
}
